import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Wifi, WifiOff, RefreshCw } from "lucide-react";
import { useNfceEmission } from "@/hooks/useNfceEmission";

interface AcbrConnectionTestProps {
  host: string;
  port: number;
  timeoutMs: number;
  onHostChange: (host: string) => void;
  onPortChange: (port: number) => void;
  onTimeoutChange: (timeoutMs: number) => void;
}

export function AcbrConnectionTest({
  host,
  port,
  timeoutMs,
  onHostChange,
  onPortChange,
  onTimeoutChange,
}: AcbrConnectionTestProps) {
  const { testAcbrConnection } = useNfceEmission();
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState<"idle" | "online" | "offline" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleTest = async () => {
    if (!host) {
      setStatus("error");
      setMessage("Informe o host/IP do ACBrMonitor");
      return;
    }

    setTesting(true);
    setStatus("idle");
    setMessage("Testando conexão...");

    const result = await testAcbrConnection(host, port, timeoutMs || 10000);

    setTesting(false);

    if (result.success && result.online) {
      setStatus("online");
      setMessage(result.message || "ACBrMonitor conectado!");
    } else if (result.success && !result.online) {
      setStatus("offline");
      // Mensagem mais clara sem referência a modo TXT/XML (problema é de protocolo)
      setMessage(result.message || "ACBrMonitor conectado, mas não respondeu ao comando. Verifique se 'Permitir Comandos Remotos' está habilitado.");
    } else {
      setStatus("error");
      setMessage(result.error || "Falha na conexão");
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="sm:col-span-2 space-y-2">
          <Label htmlFor="acbr_host">Host/IP do ACBrMonitor</Label>
          <Input
            id="acbr_host"
            value={host}
            onChange={(e) => onHostChange(e.target.value)}
            placeholder="Ex: 192.168.1.100 ou acbr.meuservidor.com"
          />
          <p className="text-xs text-muted-foreground">
            Endereço IP ou hostname onde o ACBrMonitor está instalado
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="acbr_port">Porta TCP</Label>
          <Input
            id="acbr_port"
            type="number"
            min={1}
            max={65535}
            value={port}
            onChange={(e) => onPortChange(parseInt(e.target.value) || 3434)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="acbr_timeout_ms">Timeout (ms)</Label>
          <Input
            id="acbr_timeout_ms"
            type="number"
            min={1000}
            max={300000}
            value={timeoutMs}
            onChange={(e) => onTimeoutChange(parseInt(e.target.value) || 90000)}
          />
          <p className="text-xs text-muted-foreground">
            Tempo máximo de espera por resposta do ACBr (ex.: 80000 = 80s)
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={handleTest}
          disabled={testing}
        >
          {testing ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Testar Conexão
        </Button>

        {status !== "idle" && (
          <div className="flex items-center gap-2">
            {status === "online" && (
              <Badge variant="default" className="bg-green-600 gap-1">
                <Wifi className="h-3 w-3" />
                Online
              </Badge>
            )}
            {status === "offline" && (
              <Badge variant="secondary" className="gap-1">
                <WifiOff className="h-3 w-3" />
                Offline
              </Badge>
            )}
            {status === "error" && (
              <Badge variant="destructive" className="gap-1">
                <WifiOff className="h-3 w-3" />
                Erro
              </Badge>
            )}
          </div>
        )}
      </div>

      {message && (
        <p className={`text-sm ${status === "error" ? "text-destructive" : status === "online" ? "text-green-600" : "text-muted-foreground"}`}>
          {message}
        </p>
      )}

      <div className="text-xs text-muted-foreground space-y-1 mt-4 p-3 bg-muted rounded-md">
        <p className="font-medium">Requisitos para conexão:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>ACBrMonitorPlus instalado e executando no servidor</li>
          <li>Configurado para modo TCP/IP na porta {port}</li>
          <li>Porta {port} liberada no firewall</li>
          <li>Se acessando pela internet: usar IP fixo, VPN ou túnel (ngrok)</li>
        </ul>
      </div>
    </div>
  );
}
